import { useMemo, useState } from "react";

import Box from "ds/components/Box";
import Tree, { TreeBranch, TreeBranchLink, TreeItem, TreeItemContent } from "ds/components/Tree";
import Toggle from "ds/components/Toggle";
import Typography from "ds/components/Typography";
import { ScanResourceType } from "types/generated";

import ResourcesSearchInput from "../ResourcesSearchInput";
import ResourceTypeGroupRow from "./GroupRow";
import { filterGroups, groupResourceTypes } from "./helpers";

type ResourceTypesFieldProps = {
  value: string[];
  onChange: (value: string[]) => void;
  options: Array<Pick<ScanResourceType, "type" | "subcategory">>;
  error?: string;
};

const ResourceTypesField = ({ value, onChange, options, error }: ResourceTypesFieldProps) => {
  const [search, setSearch] = useState("");
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [hasAutoExpanded, setHasAutoExpanded] = useState(false);

  const allTypes = useMemo(() => options.map(({ type }) => type), [options]);
  const groups = useMemo(() => groupResourceTypes(options), [options]);
  const selectedTypes = useMemo(() => new Set(value), [value]);

  if (!hasAutoExpanded && groups.length > 0) {
    const groupsWithSelection = groups
      .filter((group) => group.types.some((type) => selectedTypes.has(type)))
      .map((group) => group.key);

    if (groupsWithSelection.length > 0) {
      setExpandedGroups(new Set(groupsWithSelection));
    }

    setHasAutoExpanded(true);
  }

  const isSearching = !!search.trim();
  const visibleGroups = useMemo(() => filterGroups(groups, search), [groups, search]);

  const effectiveExpandedGroups = isSearching
    ? new Set(visibleGroups.map((group) => group.key))
    : expandedGroups;

  const toggleGroupExpanded = (key: string) => {
    if (isSearching) {
      return;
    }

    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const allSelected = value.length === allTypes.length;
  const someSelected = value.length > 0 && !allSelected;

  const toggleAll = () => {
    onChange(allSelected ? [] : allTypes);
  };

  const toggleType = (typeValue: string) => {
    onChange(
      selectedTypes.has(typeValue)
        ? value.filter((item) => item !== typeValue)
        : [...value, typeValue]
    );
  };

  const toggleGroup = (types: string[], select: boolean) => {
    if (select) {
      const toAdd = types.filter((type) => !selectedTypes.has(type));
      onChange([...value, ...toAdd]);
    } else {
      const typeSet = new Set(types);
      onChange(value.filter((type) => !typeSet.has(type)));
    }
  };

  return (
    <Box direction="column" gap="large" grow="1" zeroMinHeight>
      <ResourcesSearchInput
        value={search}
        onChange={setSearch}
        placeholder="Search resources"
        fullWidth
      />

      <Tree
        direction="column"
        gap="large"
        grow="1"
        zeroMinHeight
        scrollable
        aria-label="Resource types"
      >
        <TreeBranch isFirst>
          <TreeBranchLink>
            <TreeItem>
              <TreeItemContent>
                <Toggle
                  variant="checkbox"
                  typographyVariant="p-body3"
                  isSelected={allSelected}
                  isIndeterminate={someSelected}
                  onChange={toggleAll}
                >
                  All resources
                </Toggle>
                <Typography tag="span" variant="p-body3" color="secondary">
                  {value.length} of {allTypes.length} selected
                </Typography>
              </TreeItemContent>
            </TreeItem>
          </TreeBranchLink>

          <Tree direction="column" role="group" aria-label="All resources">
            {visibleGroups.map((group) => (
              <ResourceTypeGroupRow
                key={group.key}
                group={group}
                selectedTypes={selectedTypes}
                onToggleType={toggleType}
                onToggleGroup={toggleGroup}
                isExpanded={effectiveExpandedGroups.has(group.key)}
                onToggleExpanded={() => toggleGroupExpanded(group.key)}
                isExpandToggleDisabled={isSearching}
              />
            ))}

            {visibleGroups.length === 0 && (
              <Typography tag="span" variant="p-body3" color="secondary">
                No resources match your search.
              </Typography>
            )}
          </Tree>
        </TreeBranch>
      </Tree>

      {error && (
        <Typography tag="span" variant="p-body3" color="danger">
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default ResourceTypesField;
